import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Truck, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/Logo";

const NAV = [
  { label: "Find Loads", to: "/find-loads" },
  { label: "Hotshot", to: "/hotshot" },
  { label: "Ship With XD", to: "/ship" },
  { label: "For Carriers", to: "/for-carriers" },
  { label: "Marketplace", to: "/marketplace" },
  { label: "Academy", to: "/academy" },
  { label: "About", to: "/about" },
];

export function TopNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  const dashPath = user && user.role ? (user.role === "admin" ? "/admin" : user.role === "shipper" ? "/shipper" : "/dashboard") : "/login";

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors ${scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <LogoMark to="/" />

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className="text-sm font-medium text-xd-muted hover:text-white transition-colors">{n.label}</Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/notifications" data-testid="nav-notifications" className="text-xd-muted hover:text-white"><Bell size={20} /></Link>
              <Button data-testid="nav-dashboard-btn" variant="outline" className="border-xd-border text-white hover:bg-xd-surface" onClick={() => nav(dashPath)}>Dashboard</Button>
              <Button data-testid="nav-logout-btn" className="bg-xd-surface hover:bg-xd-border text-white" onClick={() => { logout(); nav("/"); }}>Logout</Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" className="text-white hover:bg-xd-surface" onClick={() => nav("/login")}>Login</Button>
              <Button data-testid="nav-join-btn" className="bg-xd-orange hover:bg-xd-orangeH text-white font-semibold" onClick={() => nav("/register")}>Join XD</Button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 px-4 py-4 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="block py-2.5 text-white font-medium">{n.label}</Link>
          ))}
          <div className="pt-3 flex gap-3">
            {user ? (
              <>
                <Button className="flex-1 bg-xd-orange text-white" onClick={() => nav(dashPath)}>Dashboard</Button>
                <Button variant="outline" className="flex-1 border-xd-border" onClick={() => { logout(); nav("/"); }}>Logout</Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="flex-1 border-xd-border text-white" onClick={() => nav("/login")}>Login</Button>
                <Button className="flex-1 bg-xd-orange text-white" onClick={() => nav("/register")}>Join XD</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
